import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import moment from 'jalali-moment'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import {
    changeCashboxDrawDate,
    deactivateCashbox,
    initiateCashboxPeriod,
    removeCashbox,
    updateCashbox,
} from 'services/cashbox'
import {
    Card,
    DAYS_OF_MONTH,
    DangerButton,
    Field,
    MoneyInput,
    Notice,
    PrimaryButton,
    SecondaryButton,
    TextInput,
    WEEK_DAYS,
    apiMessage,
    daysInJalaliMonth,
    inputClass,
    parseDigits,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox } from 'modules/cashbox/hooks'

const ANNOUNCE_OPTIONS = [
    { value: 'all', label: 'همه‌ی اعضا' },
    { value: 'jo', label: 'فقط مدیر' },
    { value: 'sil', label: 'خاموش' },
]

const JALALI_MONTHS = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
]

/**
 * تنظیمات صندوق — everything the manager can change after creation.
 *
 * `update_cashbox` demands a `period` object in the body even when only a
 * cashbox-level field changed (it 400s on "period not in data"), so every save
 * from this screen sends the full period alongside whatever was edited.
 *
 * Note on the draw date: `get_date_from_jalali_str` parses `parts[0]` as the
 * YEAR with a `-` separator, i.e. `YYYY-MM-DD`. Android's javadoc for the same
 * call claims "day-month-year", which is simply wrong — sending that silently
 * produces a nonsense date or None.
 */
export default function CashboxSettingsPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { cashbox, isLoading, isError, refetch } = useCashbox(id)

    const [name, setName] = useState('')
    const [shareValue, setShareValue] = useState(0)
    const [interval, setIntervalValue] = useState<CashboxInterval>('m')
    const [dayOfDraw, setDayOfDraw] = useState(1)
    const [numberOfLoans, setNumberOfLoans] = useState(1)
    const [ownerShares, setOwnerShares] = useState(0)
    const [managerFirstWinner, setManagerFirstWinner] = useState(false)
    const [notificationAnnounce, setNotificationAnnounce] = useState('jo')
    const [smsAnnounce, setSmsAnnounce] = useState('jo')
    const [manualCashout, setManualCashout] = useState(false)
    const [editableMembers, setEditableMembers] = useState(false)
    const [hydrated, setHydrated] = useState(false)

    const [drawYear, setDrawYear] = useState(0)
    const [drawMonth, setDrawMonth] = useState(1)
    const [drawDay, setDrawDay] = useState(1)

    useEffect(() => {
        if (!cashbox || hydrated) return
        setName(cashbox.name)
        setShareValue(cashbox.period?.share_value ?? 0)
        setIntervalValue(cashbox.period?.interval ?? 'm')
        setDayOfDraw(cashbox.period?.day_of_draw ?? 1)
        setNumberOfLoans(cashbox.period?.number_of_loans ?? 1)
        setOwnerShares(cashbox.membership?.number_of_shares ?? 0)
        setManagerFirstWinner(
            cashbox.period?.is_manager_first_winner ?? false
        )
        setNotificationAnnounce(cashbox.notification_announce ?? 'jo')
        setSmsAnnounce(cashbox.sms_announce ?? 'jo')
        setManualCashout(cashbox.manual_cashout ?? false)
        setEditableMembers(cashbox.editable_members ?? false)

        const current = cashbox.cycle?.draw_date_jalali
        const parsed = current ? current.split('-').map(Number) : []
        const today = moment().locale('fa')
        setDrawYear(parsed[0] || today.jYear())
        setDrawMonth(parsed[1] || today.jMonth() + 1)
        setDrawDay(parsed[2] || today.jDate())
        setHydrated(true)
    }, [cashbox, hydrated])

    const invalidate = () => {
        queryClient.invalidateQueries(CASHBOX_KEY(id))
        queryClient.invalidateQueries(['get-boxes'])
    }

    const save = useMutation({
        mutationFn: () =>
            updateCashbox({
                id: id!,
                name: name.trim(),
                image_id: cashbox?.image_id ?? 1,
                notification_announce: notificationAnnounce,
                sms_announce: smsAnnounce,
                manual_cashout: manualCashout,
                editable_members: editableMembers,
                number_of_shares: ownerShares,
                period: {
                    share_value: shareValue,
                    interval,
                    day_of_draw: dayOfDraw,
                    number_of_loans: numberOfLoans,
                    is_manager_first_winner: managerFirstWinner,
                },
            }),
        onSuccess: () => {
            toast.success('تنظیمات ذخیره شد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const changeDate = useMutation({
        mutationFn: () =>
            changeCashboxDrawDate({
                id: id!,
                jalaliDate: `${drawYear}-${drawMonth}-${drawDay}`,
            }),
        onSuccess: () => {
            toast.success('تاریخ قرعه‌کشی تغییر کرد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const deactivate = useMutation({
        mutationFn: () => deactivateCashbox(id!),
        onSuccess: () => {
            toast.success('صندوق غیرفعال شد')
            invalidate()
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const newPeriod = useMutation({
        mutationFn: () => initiateCashboxPeriod(id!),
        onSuccess: () => {
            toast.success('دوره‌ی جدید شروع شد')
            invalidate()
            navigate(`/cashbox/${id}/members`)
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    const destroy = useMutation({
        mutationFn: () => removeCashbox(id!),
        onSuccess: () => {
            toast.success('صندوق حذف شد')
            queryClient.invalidateQueries(['get-boxes'])
            navigate('/funds')
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    if (isLoading || !hydrated) return <DotsLoading />
    if (isError || !cashbox)
        return (
            <div className="bg-white min-h-screen">
                <Header title="تنظیمات صندوق" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="تنظیمات صندوق" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق به تنظیمات دسترسی دارد.
                    </Notice>
                </div>
            </div>
        )

    const isActive = cashbox.state === 'act'
    const pastFirstCycle = (cashbox.period?.cycle_index ?? 0) > 1
    const periodLocked = isActive && pastFirstCycle
    const drawn = Boolean(cashbox.cycle?.drawing_date_jalali)

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-10">
            <div className="bg-white">
                <Header title="تنظیمات صندوق" />
            </div>

            <div className="px-4 pt-3">
                {periodLocked && (
                    <div className="mb-3">
                        <Notice tone="warn">
                            صندوق در جریان است. تغییر ارزش سهم، تعداد وام و
                            دوره‌ی قرعه‌کشی روی نوبت‌های بعدی اثر می‌گذارد و
                            ممکن است صندوق را از تعادل خارج کند؛ با احتیاط
                            تغییر دهید.
                        </Notice>
                    </div>
                )}

                <p className="text-sm font-bold text-black mb-2">
                    اطلاعات صندوق
                </p>
                <Card className="mb-4">
                    <Field label="نام صندوق">
                        <TextInput
                            value={name}
                            maxLength={100}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>
                    <Field label="ارزش هر سهم">
                        <MoneyInput value={shareValue} onChange={setShareValue} />
                    </Field>
                    <Field label="تعداد وام در هر نوبت">
                        <TextInput
                            inputMode="numeric"
                            value={String(numberOfLoans)}
                            onChange={(event) =>
                                setNumberOfLoans(parseDigits(event.target.value))
                            }
                        />
                    </Field>
                    <Field label="تعداد سهم شما">
                        <TextInput
                            inputMode="numeric"
                            value={String(ownerShares)}
                            onChange={(event) =>
                                setOwnerShares(parseDigits(event.target.value))
                            }
                        />
                    </Field>
                    <Field label="دوره‌ی قرعه‌کشی">
                        <div className="flex gap-2">
                            {(
                                [
                                    { value: 'm', label: 'ماهانه' },
                                    { value: 'w', label: 'هفتگی' },
                                ] as Array<{
                                    value: CashboxInterval
                                    label: string
                                }>
                            ).map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() =>
                                        setIntervalValue(option.value)
                                    }
                                    className={`flex-1 h-11 rounded-[10px] border text-sm ${
                                        interval === option.value
                                            ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                            : 'border-divider bg-white text-text-body'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Field
                        label={
                            interval === 'w'
                                ? 'روز قرعه‌کشی در هفته'
                                : 'روز قرعه‌کشی در ماه'
                        }
                    >
                        <select
                            value={dayOfDraw}
                            onChange={(event) =>
                                setDayOfDraw(Number(event.target.value))
                            }
                            className={inputClass}
                        >
                            {interval === 'w'
                                ? WEEK_DAYS.map((day) => (
                                      <option key={day.value} value={day.value}>
                                          {day.label}
                                      </option>
                                  ))
                                : DAYS_OF_MONTH.map((day) => (
                                      <option key={day} value={day}>
                                          {faPlain.format(day)}
                                      </option>
                                  ))}
                        </select>
                    </Field>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={managerFirstWinner}
                            onChange={(event) =>
                                setManagerFirstWinner(event.target.checked)
                            }
                            className="w-4 h-4 mt-0.5 accent-green-1"
                        />
                        <span className="text-sm text-black">
                            برنده‌ی نوبت اول، مدیر صندوق باشد
                        </span>
                    </label>
                </Card>

                <p className="text-sm font-bold text-black mb-2">اطلاع‌رسانی</p>
                <Card className="mb-4">
                    <Field label="اعلان‌ها برای">
                        <select
                            value={notificationAnnounce}
                            onChange={(event) =>
                                setNotificationAnnounce(event.target.value)
                            }
                            className={inputClass}
                        >
                            {ANNOUNCE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="پیامک برای">
                        <select
                            value={smsAnnounce}
                            onChange={(event) =>
                                setSmsAnnounce(event.target.value)
                            }
                            className={inputClass}
                        >
                            {ANNOUNCE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </Field>
                </Card>

                <p className="text-sm font-bold text-black mb-2">
                    اختیارات مدیر
                </p>
                <Card className="mb-4">
                    <label className="flex items-start gap-2 cursor-pointer mb-3">
                        <input
                            type="checkbox"
                            checked={editableMembers}
                            onChange={(event) =>
                                setEditableMembers(event.target.checked)
                            }
                            className="w-4 h-4 mt-0.5 accent-green-1"
                        />
                        <span>
                            <span className="block text-sm text-black">
                                امکان تغییر اعضا پس از شروع
                            </span>
                            <span className="block text-xs text-text-secondary mt-0.5">
                                بدون این گزینه، از نوبت دوم به بعد نمی‌توانید
                                عضو اضافه یا حذف کنید
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={manualCashout}
                            onChange={(event) =>
                                setManualCashout(event.target.checked)
                            }
                            className="w-4 h-4 mt-0.5 accent-green-1"
                        />
                        <span>
                            <span className="block text-sm text-black">
                                پرداخت دستی وام
                            </span>
                            <span className="block text-xs text-text-secondary mt-0.5">
                                امکان پرداخت وام به یک عضو بدون قرعه‌کشی
                            </span>
                        </span>
                    </label>
                </Card>

                <PrimaryButton
                    disabled={save.isLoading || name.trim().length < 3}
                    onClick={() => save.mutate()}
                >
                    {save.isLoading ? 'در حال ذخیره…' : 'ذخیره تنظیمات'}
                </PrimaryButton>

                {/* draw date ------------------------------------------- */}
                {isActive && (
                    <>
                        <p className="text-sm font-bold text-black mb-2 mt-6">
                            تاریخ قرعه‌کشی نوبت جاری
                        </p>
                        <Card className="mb-4">
                            {drawn ? (
                                <p className="text-xs text-text-secondary">
                                    قرعه‌کشی این نوبت انجام شده و تاریخ آن قابل
                                    تغییر نیست.
                                </p>
                            ) : (
                                <>
                                    <p className="text-xs text-text-secondary mb-2">
                                        تاریخ فعلی:{' '}
                                        {cashbox.cycle?.draw_date_jalali || '—'}
                                    </p>
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <select
                                            value={drawYear}
                                            onChange={(event) =>
                                                setDrawYear(
                                                    Number(event.target.value)
                                                )
                                            }
                                            className={inputClass}
                                        >
                                            {[0, 1].map((offset) => {
                                                const year =
                                                    moment().locale('fa').jYear() +
                                                    offset
                                                return (
                                                    <option
                                                        key={year}
                                                        value={year}
                                                    >
                                                        {faPlain.format(year)}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                        <select
                                            value={drawMonth}
                                            onChange={(event) =>
                                                setDrawMonth(
                                                    Number(event.target.value)
                                                )
                                            }
                                            className={inputClass}
                                        >
                                            {JALALI_MONTHS.map(
                                                (month, index) => (
                                                    <option
                                                        key={month}
                                                        value={index + 1}
                                                    >
                                                        {month}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                        <select
                                            value={drawDay}
                                            onChange={(event) =>
                                                setDrawDay(
                                                    Number(event.target.value)
                                                )
                                            }
                                            className={inputClass}
                                        >
                                            {/* A one-off date CAN be the 30th
                                                or 31st — but only in a month
                                                that has one, so the options
                                                follow the chosen month. */}
                                            {Array.from(
                                                {
                                                    length: daysInJalaliMonth(
                                                        drawYear,
                                                        drawMonth
                                                    ),
                                                },
                                                (_, i) => i + 1
                                            ).map((day) => (
                                                <option key={day} value={day}>
                                                    {faPlain.format(day)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <SecondaryButton
                                        disabled={changeDate.isLoading}
                                        onClick={() => changeDate.mutate()}
                                    >
                                        تغییر تاریخ قرعه‌کشی
                                    </SecondaryButton>
                                </>
                            )}
                        </Card>
                    </>
                )}

                {/* lifecycle -------------------------------------------- */}
                <p className="text-sm font-bold text-black mb-2 mt-6">
                    وضعیت صندوق
                </p>
                <Card className="mb-4">
                    <p className="text-xs text-text-secondary mb-3">
                        موجودی فعلی: {toman(cashbox.balance)}
                    </p>

                    {cashbox.period?.is_terminated && (
                        <div className="mb-3">
                            <SecondaryButton
                                disabled={newPeriod.isLoading}
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'دوره‌ی جدید شروع شود؟ اعضا باید دوباره اضافه شوند.'
                                        )
                                    )
                                        newPeriod.mutate()
                                }}
                            >
                                شروع دوره‌ی جدید
                            </SecondaryButton>
                        </div>
                    )}

                    {isActive && (
                        <div className="mb-3">
                            <SecondaryButton
                                disabled={deactivate.isLoading}
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'صندوق غیرفعال شود؟ تا فعال‌سازی مجدد، پرداخت و قرعه‌کشی متوقف می‌شود.'
                                        )
                                    )
                                        deactivate.mutate()
                                }}
                            >
                                غیرفعال کردن صندوق
                            </SecondaryButton>
                        </div>
                    )}

                    <DangerButton
                        disabled={destroy.isLoading}
                        onClick={() => {
                            if (
                                window.confirm(
                                    'صندوق حذف شود؟ این کار برگشت‌پذیر نیست.'
                                )
                            )
                                destroy.mutate()
                        }}
                    >
                        حذف صندوق
                    </DangerButton>
                    <p className="text-xs text-text-secondary mt-2">
                        صندوقی که تراکنش موفق دارد یا اشتراک فعالش باقی‌مانده،
                        حذف نمی‌شود.
                    </p>
                </Card>
            </div>
        </div>
    )
}
